import { useEffect, useRef } from 'react';

/**
 * useFocusTrap — modal 의 focus trap + restore 표준 hook.
 *
 * adr-20260506-focus-trap-hook-without-native-dialog §2.1. useModalLeave 와
 * 동일 철학 (active boolean + ref 출력). active=true 동안 container 내
 * Tab/Shift+Tab 순환 + container 안 첫 focusable 자동 focus + active=false 전이
 * 시 mount 직전의 trigger element 로 focus 복귀.
 *
 * 사용:
 *   const { shouldRender } = useModalLeave(open, 200);
 *   const trapRef = useFocusTrap(shouldRender);
 *   {shouldRender && <div ref={trapRef} role="dialog" aria-modal="true">...</div>}
 *
 * 첫 focusable 결정:
 *   - mount 직후 container 안에 이미 focus 가 있으면 유지 (React 의 autoFocus prop
 *     이 mount 시 자동 focus 호출하므로 자연 보존). modal 별 form 첫 input 에
 *     `autoFocus` 추가만으로 첫 focusable 명시 가능
 *   - 그렇지 않으면 [autofocus] 속성 element 우선, 없으면 querySelectorAll(FOCUSABLE)[0]
 *
 * leaving phase 와의 결합:
 *   active 입력으로 useModalLeave 의 shouldRender 를 그대로 연결 — leaving phase
 *   (shouldRender=true + isLeaving=true) 동안에도 trap 유지. 사용자가 Tab 으로
 *   외부 element 이동 차단.
 *
 * trigger restore:
 *   cleanup 시 mount 직전 캐싱된 element 가 여전히 document 안에 있으면 focus()
 *   호출. unmount 된 button 같은 stale element 에는 focus 안 줌 (R3 봉합).
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(
  active: boolean,
): React.RefObject<T> {
  const containerRef = useRef<T>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement;
    // container 안에 이미 focus 가 있으면 trigger 캐싱 skip — autoFocus prop 이
    // mount 시 자동 focus 호출한 경우 previouslyFocused 가 container 안 element
    // 가 됨. 이 경우 trigger 는 hook mount 이전의 외부 element 가 아니라 form
    // input 자체 → 복귀 의미 없음.
    const triggerIsExternal =
      previouslyFocused instanceof HTMLElement && !container.contains(previouslyFocused);
    if (triggerIsExternal) {
      triggerRef.current = previouslyFocused as HTMLElement;
    }

    // container 안에 이미 focus (autoFocus prop) 있으면 유지, 아니면 [autofocus]
    // 속성 또는 첫 focusable.
    if (!container.contains(document.activeElement)) {
      const explicitAutoFocus = container.querySelector<HTMLElement>('[autofocus]');
      if (explicitAutoFocus) {
        explicitAutoFocus.focus();
      } else {
        const focusables = container.querySelectorAll<HTMLElement>(FOCUSABLE);
        focusables[0]?.focus();
      }
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const activeEl = document.activeElement;
      if (e.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    container.addEventListener('keydown', onKeyDown);

    return () => {
      container.removeEventListener('keydown', onKeyDown);
      const trigger = triggerRef.current;
      if (trigger && document.contains(trigger)) {
        trigger.focus();
      }
      triggerRef.current = null;
    };
  }, [active]);

  return containerRef;
}
